import { formatDate, toTitleCase } from "@luce/core";
import type { IconName } from "@luce/ui";

import { DiscountTypeEnum, PackageDepartmentEnum } from "../../__generated__/graphql";
import { DEPARTMENT_LABELS } from "../../shared";
import type { PromoCode } from "../gqlhooks/use-available-promo-codes";

/** "10% off home cleaning" / "$20 off your booking" format, since BE didnt provide a consistent format */
export function promoTitle(
  promo: Pick<PromoCode, "discountType" | "discountValue" | "departments">,
): string {
  const discount =
    promo.discountType === DiscountTypeEnum.Percent
      ? `${promo.discountValue}% off`
      : `$${promo.discountValue} off`;

  const target =
    promo.departments?.length === 1 ? DEPARTMENT_LABELS[promo.departments[0]!] : "your booking";

  return `${discount} ${target}`;
}

/** Bare date — "30 Jun 2026" — for the promo card, which labels it separately. */
export function promoExpiryDate(validUntil: string | null | undefined): string | null {
  if (!validUntil) return null;
  return formatDate(new Date(validUntil), "d MMM yyyy");
}

/** Same date, self-labelling — "Expires 30 Jun 2026" — for the promo list row. */
export function promoExpiry(validUntil: string | null | undefined): string | null {
  const date = promoExpiryDate(validUntil);
  return date ? `Expires ${date}` : null;
}

/** Title-cased department the promo applies to, or "Your booking" when it spans many/none. */
export function promoDepartmentLabel(promo: Pick<PromoCode, "departments">): string {
  if (promo.departments?.length === 1) {
    return toTitleCase(DEPARTMENT_LABELS[promo.departments[0]!]);
  }
  return "All Service";
}

const DEPARTMENT_ICON: Partial<Record<PackageDepartmentEnum, IconName>> = {
  [PackageDepartmentEnum.HomeCleaning]: "House",
  [PackageDepartmentEnum.DeepCleaning]: "House",
  [PackageDepartmentEnum.OfficeCleaning]: "House",
  [PackageDepartmentEnum.CarpetUpholstery]: "House",
  [PackageDepartmentEnum.PetCare]: "PawPrint",
  [PackageDepartmentEnum.ElderCare]: "UsersThree",
  [PackageDepartmentEnum.Babysitter]: "UsersThree",
  [PackageDepartmentEnum.HomeBeauty]: "FlowerLotus",
  [PackageDepartmentEnum.Massage]: "FlowerLotus",
  [PackageDepartmentEnum.Security]: "ShieldCheck",
};

/** Icon for the department chip, falling back to a generic discount marker. */
export function promoDepartmentIcon(promo: Pick<PromoCode, "departments">): IconName {
  const single = promo.departments?.length === 1 ? promo.departments[0]! : null;
  return (single && DEPARTMENT_ICON[single]) || "SealPercent";
}
